import React, { useEffect, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  GetBankDetailsByUserId,
  GetMasterCompany,
  PaidWallet,
  updateWalletById,
  downloadFileByKey,
  downloadFileBase64ByKey,
  base64toBlob,
} from "../../Service/_index";
import { ToastError, ToastSuccess } from "../../UiComponents/Toaster/Toast";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import dayjs from "dayjs";
import Tooltip from "@mui/material/Tooltip";
import { Box, Tab, Tabs } from "@mui/material";
import {
  CloseIcon,
  DownloadIcon,
  RemoveRedEyeIcon,
} from "../../Resources/Icons/icons";

const styles = {
  label: {
    fontSize: "12px",
    fontFamily: "Inter, sans-serif",
    fontWeight: 400,
    marginBottom: "4px",
    display: "block",
  },
  input: {
    borderRadius: "6px",
    height: 38,
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      height: 38,
    },
  },
  errorText: {
    color: "#d32f2f",
    fontSize: "11px",
    marginTop: "3px",
    fontFamily: "Inter, sans-serif",
  },
};

// ─── Yup Validation Schemas ────────────────────────────────────────────────

const withTDSSchema = Yup.object().shape({
  transactionDate: Yup.date()
    .nullable()
    .required("Transaction date is required"),
  accountNumber: Yup.string()
    .required("Account holder is required"),
  bankName: Yup.string()
    .required("Bank name is required"),
  panNumber: Yup.string()
    .required("PAN number is required"),
  paymentType: Yup.string()
    .required("Payment mode is required"),
  walletCompanyId: Yup.string()
    .required("Company is required"),
  amount: Yup.number()
    .typeError("Amount must be a number")
    .required("Amount is required")
    .min(1, "Amount must be at least 1"),
});

const withoutTDSSchema = Yup.object().shape({
  transactionDate: Yup.date()
    .nullable()
    .required("Transaction date is required"),
  walletCompanyId: Yup.string()
    .required("Company is required"),
  amount: Yup.number()
    .typeError("Amount must be a number")
    .required("Amount is required")
    .min(1, "Amount must be at least 1"),
});

const receivedSchema = Yup.object().shape({
  transactionDate: Yup.date()
    .nullable()
    .required("Transaction date is required"),
  bankName: Yup.string()
    .required("Bank name is required"),
  walletCompanyId: Yup.string()
    .required("Company is required"),
  amount: Yup.number()
    .typeError("Amount must be a number")
    .required("Amount is required")
    .min(1, "Amount must be at least 1"),
});

// ─── With TDS Tab Component ────────────────────────────────────────────────

const TDSComponent = ({
  values,
  errors,
  touched,
  setFieldValue,
  setFieldTouched,
  bankDetails,
  selectedAccHolderName,
  AccountHandleFunction,
  PaymentType,
  companyDetails,
  walletCompany,
  setWalletCompany,
  localAmount,
  setLocalAmount,
  AmountCalculateFunction,
  localTDS,
  setLocalTDS,
  TDSCalculateFunction,
  selectedBank,
  showBankDetail,
  setShowBankDetail,
  setDrawerWidth,
}) => {
  return (
    <>
      <Grid container spacing={1} mt={1}>
        {/* Transaction Date */}
        <Grid item xs={12} sm={6}>
          <label htmlFor="transactionDate" style={styles.label}>
            Transaction Date <span style={{ color: "red" }}>*</span>
          </label>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              name="transactionDate"
              id="transactionDate"
              className="Date_Picker w-100"
              format="DD/MM/YYYY"
              value={dayjs(values.transactionDate)}
              onChange={(e) => {
                setFieldValue("transactionDate", e?.$d || null);
                setFieldTouched("transactionDate", true);
              }}
              sx={{
                ...styles.input,
                "& .MuiOutlinedInput-root": {
                  borderColor:
                    errors.transactionDate && touched.transactionDate
                      ? "#d32f2f"
                      : undefined,
                },
              }}
            />
          </LocalizationProvider>
          {errors.transactionDate && touched.transactionDate && (
            <span style={styles.errorText}>{errors.transactionDate}</span>
          )}
        </Grid>

        {/* Account Holder */}
        <Grid item xs={12} sm={6}>
          <label htmlFor="accountNumber" style={styles.label}>
            Account Holder <span style={{ color: "red" }}>*</span>
          </label>
          <Autocomplete
            className="AutoComplete_InputBox w-100"
            name="accountNumber"
            id="accountNumber"
            onChange={(e, v) => {
              AccountHandleFunction(v, setFieldValue);
              setFieldTouched("accountNumber", true);
            }}
            options={bankDetails || []}
            value={
              bankDetails.find(
                (bd) => bd.bankAccountHolderName === selectedAccHolderName
              ) || null
            }
            clearIcon={false}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  ...styles.input,
                  "& .MuiOutlinedInput-root fieldset": {
                    borderColor:
                      errors.accountNumber && touched.accountNumber
                        ? "#d32f2f"
                        : undefined,
                  },
                }}
              />
            )}
            isOptionEqualToValue={(option, value) =>
              option.accountNumber === value?.accountNumber
            }
          />
          {errors.accountNumber && touched.accountNumber && (
            <span style={styles.errorText}>{errors.accountNumber}</span>
          )}
        </Grid>

        {/* Bank */}
        <Grid item xs={12} sm={6}>
          <label htmlFor="bankName" style={styles.label}>
            Bank <span style={{ color: "red" }}>*</span>
          </label>
          <Field
            name="bankName"
            id="bankName"
            className="InputFiled"
            style={{
              ...styles.input,
              borderColor:
                errors.bankName && touched.bankName ? "#d32f2f" : undefined,
            }}
          />
          {errors.bankName && touched.bankName && (
            <span style={styles.errorText}>{errors.bankName}</span>
          )}
        </Grid>

        {/* Pan Number */}
        <Grid item xs={12} sm={6}>
          <label htmlFor="panNumber" style={styles.label}>
            Pan Number <span style={{ color: "red" }}>*</span>
          </label>
          <Field
            name="panNumber"
            id="panNumber"
            className="InputFiled"
            style={{
              textTransform: "uppercase",
              ...styles.input,
              borderColor:
                errors.panNumber && touched.panNumber ? "#d32f2f" : undefined,
            }}
          />
          {errors.panNumber && touched.panNumber && (
            <span style={styles.errorText}>{errors.panNumber}</span>
          )}
        </Grid>

        {/* Payment Mode */}
        <Grid item xs={12} sm={6}>
          <label htmlFor="paymentType" style={styles.label}>
            Payment Mode <span style={{ color: "red" }}>*</span>
          </label>
          <Autocomplete
            className="AutoComplete_InputBox w-100"
            name="paymentType"
            id="paymentType"
            onChange={(e, v) => {
              setFieldValue("paymentType", v?.value);
              setFieldTouched("paymentType", true);
            }}
            value={
              PaymentType.find((pt) => pt.value === values.paymentType) || null
            }
            options={PaymentType}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  ...styles.input,
                  "& .MuiOutlinedInput-root fieldset": {
                    borderColor:
                      errors.paymentType && touched.paymentType
                        ? "#d32f2f"
                        : undefined,
                  },
                }}
              />
            )}
          />
          {errors.paymentType && touched.paymentType && (
            <span style={styles.errorText}>{errors.paymentType}</span>
          )}
        </Grid>

        {/* Remark */}
        <Grid item xs={12} sm={6}>
          <label htmlFor="remark" style={styles.label}>
            Remark
          </label>
          <Field
            name="remark"
            id="remark"
            className="InputFiled"
            style={styles.input}
          />
        </Grid>

        {/* Select Company */}
        <Grid item xs={12} sm={6}>
          <label htmlFor="walletCompanyId" style={styles.label}>
            Select Company <span style={{ color: "red" }}>*</span>
          </label>
          <Autocomplete
            className="AutoComplete_InputBox w-100"
            name="walletCompanyId"
            id="walletCompanyId"
            onChange={(e, v) => {
              setFieldValue("walletCompanyId", v?._id);
              setWalletCompany(v?.masterCompanyName);
              setFieldTouched("walletCompanyId", true);
            }}
            value={
              companyDetails.find(
                (cd) => cd.masterCompanyName === walletCompany
              ) || null
            }
            options={companyDetails}
            clearIcon={false}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  ...styles.input,
                  "& .MuiOutlinedInput-root fieldset": {
                    borderColor:
                      errors.walletCompanyId && touched.walletCompanyId
                        ? "#d32f2f"
                        : undefined,
                  },
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option._id === value?._id}
          />
          {errors.walletCompanyId && touched.walletCompanyId && (
            <span style={styles.errorText}>{errors.walletCompanyId}</span>
          )}
        </Grid>

        {/* Amount */}
        <Grid item xs={12} sm={6}>
          <label htmlFor="amount" style={styles.label}>
            Amount <span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            name="amount"
            id="amount"
            className="InputFiled"
            style={{
              ...styles.input,
              borderColor:
                errors.amount && touched.amount ? "#d32f2f" : undefined,
            }}
            value={localAmount}
            onChange={(e) => {
              const val = e.target.value;
              setLocalAmount(val);
              setFieldValue("amount", val);
              setFieldTouched("amount", true);
              AmountCalculateFunction(val, setFieldValue);
            }}
          />
          {errors.amount && touched.amount && (
            <span style={styles.errorText}>{errors.amount}</span>
          )}
        </Grid>

        {/* TDS (%) */}
        <Grid item xs={12} sm={6}>
          <label htmlFor="TDS" style={styles.label}>
            TDS (%)
          </label>
          <input
            type="text"
            name="TDS"
            id="TDS"
            className="InputFiled"
            value={localTDS}
            style={styles.input}
            onChange={(e) => {
              const val = e.target.value;
              setLocalTDS(val);
              setFieldValue("TDS", val);
              TDSCalculateFunction(val, setFieldValue);
            }}
          />
        </Grid>

        {/* TDS Amount */}
        <Grid item xs={12} sm={6}>
          <label htmlFor="TDSamount" style={styles.label}>
            TDS Amount
          </label>
          <Field
            name="TDSamount"
            id="TDSamount"
            className="InputFiled"
            style={styles.input}
            disabled
          />
        </Grid>

        {/* Transfer Amount */}
        <Grid item xs={12} sm={6}>
          <label htmlFor="TransferAmount" style={styles.label}>
            Transfer Amount
          </label>
          <Field
            name="TransferAmount"
            id="TransferAmount"
            className="InputFiled"
            style={styles.input}
            disabled
          />
        </Grid>
      </Grid>

      {selectedBank && (
        <Grid item xs={12} sm={12} mt={3}>
          <Button
            onClick={() => {
              setDrawerWidth(showBankDetail ? "50%" : "60%");
              setShowBankDetail(!showBankDetail);
            }}
            style={{
              background: showBankDetail ? "#1E3A5F" : "#667085",
              padding: "8px 4px",
              color: "white",
              borderRadius: "6px",
              textTransform: "none",
              // fontSize: "4px",
            }}
          >
            {showBankDetail ? "Hide Bank Details" : "View Bank Details"}
          </Button>
        </Grid>
      )}
    </>
  );
};

// ─── Without TDS Tab Component ─────────────────────────────────────────────

const WithoutTDSComponent = ({
  values,
  errors,
  touched,
  setFieldValue,
  setFieldTouched,
  companyDetails,
  walletCompany,
  setWalletCompany,
  setAmountVal,
}) => {
  return (
    <Grid container spacing={1} mt={1}>
      {/* Transaction Date */}
      <Grid item xs={12} sm={6}>
        <label htmlFor="transactionDate" style={styles.label}>
          Transaction Date <span style={{ color: "red" }}>*</span>
        </label>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            name="transactionDate"
            id="transactionDate"
            className="Date_Picker w-100"
            format="DD/MM/YYYY"
            value={dayjs(values.transactionDate)}
            onChange={(e) => {
              setFieldValue("transactionDate", e?.$d || null);
              setFieldTouched("transactionDate", true);
            }}
            sx={styles.input}
          />
        </LocalizationProvider>
        {errors.transactionDate && touched.transactionDate && (
          <span style={styles.errorText}>{errors.transactionDate}</span>
        )}
      </Grid>

      {/* Remark */}
      <Grid item xs={12} sm={6}>
        <label htmlFor="remark" style={styles.label}>
          Remark
        </label>
        <Field
          name="remark"
          id="remark"
          className="InputFiled"
          style={styles.input}
        />
      </Grid>

      {/* Select Company */}
      <Grid item xs={12} sm={6}>
        <label htmlFor="walletCompanyId" style={styles.label}>
          Select Company <span style={{ color: "red" }}>*</span>
        </label>
        <Autocomplete
          className="AutoComplete_InputBox w-100"
          name="walletCompanyId"
          id="walletCompanyId"
          onChange={(e, v) => {
            setFieldValue("walletCompanyId", v?._id);
            setWalletCompany(v?.masterCompanyName);
            setFieldTouched("walletCompanyId", true);
          }}
          value={
            companyDetails.find(
              (cd) => cd.masterCompanyName === walletCompany
            ) || null
          }
          options={companyDetails}
          clearIcon={false}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{
                ...styles.input,
                "& .MuiOutlinedInput-root fieldset": {
                  borderColor:
                    errors.walletCompanyId && touched.walletCompanyId
                      ? "#d32f2f"
                      : undefined,
                },
              }}
            />
          )}
          isOptionEqualToValue={(option, value) => option._id === value?._id}
        />
        {errors.walletCompanyId && touched.walletCompanyId && (
          <span style={styles.errorText}>{errors.walletCompanyId}</span>
        )}
      </Grid>

      {/* Amount */}
      <Grid item xs={12} sm={6}>
        <label htmlFor="amount" style={styles.label}>
          Amount <span style={{ color: "red" }}>*</span>
        </label>
        <TextField
          name="amount"
          id="amount"
          className="InputFiled"
          sx={{
            ...styles.input,
            "& .MuiOutlinedInput-root fieldset": {
              borderColor:
                errors.amount && touched.amount ? "#d32f2f" : undefined,
            },
          }}
          value={values?.amount}
          onChange={(e) => {
            setFieldValue("amount", e.target.value);
            setFieldValue("TransferAmount", e.target.value);
            setAmountVal(parseFloat(e.target.value) || 0);
            setFieldTouched("amount", true);
          }}
        />
        {errors.amount && touched.amount && (
          <span style={styles.errorText}>{errors.amount}</span>
        )}
      </Grid>

    </Grid>
  );
};

// ─── Bank Details Panel ────────────────────────────────────────────────────

const BankDetailsPanel = ({
  selectedBank,
  showBankDetail,
  downloadFile,
  viewFile,
}) => {
  if (!selectedBank || !showBankDetail) return null;

  return (
    <Grid item xs={12} sm={12} mt={3}>
      <Typography variant="h6" gutterBottom>
        Bank Details of {selectedBank?.bankAccountHolderName}
      </Typography>
      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} sm={6}>
          <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
            Name
          </Typography>
          <Typography sx={{ fontSize: "13px" }}>
            {selectedBank?.bankAccountHolderName}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
            Bank Name
          </Typography>
          <Typography sx={{ fontSize: "13px" }}>
            {selectedBank?.bankName}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
            Account Number
          </Typography>
          <Typography sx={{ fontSize: "13px" }}>
            {selectedBank?.accountNumber}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
            Bank Branch
          </Typography>
          <Typography sx={{ fontSize: "13px" }}>
            {selectedBank?.bankBranch}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
            GST Number
          </Typography>
          <Typography sx={{ fontSize: "13px" }}>
            {selectedBank?.gstNumber}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
            Pan Number
          </Typography>
          <Typography sx={{ fontSize: "13px" }}>
            {selectedBank?.panNumber}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
            Ifsc Code
          </Typography>
          <Typography sx={{ fontSize: "13px" }}>
            {selectedBank?.ifscCode}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
            Micr Number
          </Typography>
          <Typography sx={{ fontSize: "13px" }}>
            {selectedBank?.micrNumber}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
            PAN Card
          </Typography>
          <Tooltip title="Download PAN card">
            <DownloadIcon
              sx={{ cursor: "pointer", mr: 1 }}
              onClick={() => downloadFile(selectedBank?.PANCardProof?.key)}
            />
          </Tooltip>
          <Tooltip title="View PAN card">
            <RemoveRedEyeIcon
              sx={{ cursor: "pointer" }}
              onClick={() => viewFile(selectedBank?.PANCardProof?.key)}
            />
          </Tooltip>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
            Passbook
          </Typography>
          <Tooltip title="Download Passbook">
            <DownloadIcon
              sx={{ cursor: "pointer", mr: 1 }}
              onClick={() => downloadFile(selectedBank?.PassBookProof?.key)}
            />
          </Tooltip>
          <Tooltip title="View Passbook">
            <RemoveRedEyeIcon
              sx={{ cursor: "pointer" }}
              onClick={() => viewFile(selectedBank?.PassBookProof?.key)}
            />
          </Tooltip>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
            Aadhar Proof
          </Typography>
          <Tooltip title="Download Aadhar">
            <DownloadIcon
              sx={{ cursor: "pointer", mr: 1 }}
              onClick={() => downloadFile(selectedBank?.AadharProof?.key)}
            />
          </Tooltip>
          <Tooltip title="View Aadhar">
            <RemoveRedEyeIcon
              sx={{ cursor: "pointer" }}
              onClick={() => viewFile(selectedBank?.AadharProof?.key)}
            />
          </Tooltip>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
            GST Proof
          </Typography>
          <Tooltip title="Download GST Copy">
            <DownloadIcon
              sx={{ cursor: "pointer", mr: 1 }}
              onClick={() => downloadFile(selectedBank?.GSTProof?.key)}
            />
          </Tooltip>
          <Tooltip title="View GST Copy">
            <RemoveRedEyeIcon
              sx={{ cursor: "pointer" }}
              onClick={() => viewFile(selectedBank?.GSTProof?.key)}
            />
          </Tooltip>
        </Grid>
      </Grid>
    </Grid>
  );
};

// ─── Main PaidTransaction Component ───────────────────────────────────────

const PaidTransaction = (props) => {
  console.log("🔄 PaidTransaction component rendered");
  const UserId = sessionStorage.getItem("UserId");

  const {
    selectedData,
    isViewPaidTransaction,
    GetData,
    setOpenPaidTransaction,
    GetUserWalletDetails,
    PaidUserName,
    functionType,
    setDrawerWidth,
    callBackFunction,
  } = props;

  const formRef = useRef();
  const [value, setValue] = useState(0); // 0 = With TDS, 1 = Without TDS
  const [showBankDetail, setShowBankDetail] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [selectedAccHolderName, setSelectedAccHolderName] = useState(null);
  const [companyDetails, setCompanyDetails] = useState([]);
  const [bankDetails, setBankDetails] = useState([]);
  const [amountVal, setAmountVal] = useState(null);
  const [tdsAmount, setTdsAmount] = useState(null);
  const [walletCompany, setWalletCompany] = useState(null);
  const calculationTimeoutRef = useRef(null);
  const [localAmount, setLocalAmount] = useState("");
  const [localTDS, setLocalTDS] = useState("");

  const initialValues = {
    walletCompanyId: "",
    amount: "",
    TDS: "",
    transactionDate: new Date(),
    TDSamount: "",
    TransferAmount: "",
    accountNumber: "",
    bankName: "",
    paymentType: "",
    remark: "",
    panNumber: "",
  };

  const PaymentType = [
    {
      label: "With TDS",
      value: "With TDS",
    },
    {
      label: "With GST",
      value: "With GST",
    },
  ];

  // Pick schema based on active tab
  const activeSchema = value === 0 ? withTDSSchema : withoutTDSSchema;

  const onSubmit = async (e) => {
    e.isUserPayable = true;
    const RandomNumber = await getRandomNumberUsingTimestamp();
    e.userId = selectedData.userId;
    e.policyNumber = RandomNumber.toString();
    e.paymentMode = functionType === "Paid" ? "PAID" : "RECEIVED";
    e.createdBy = UserId;
    e.withTDS = value === 0; // 0 = With TDS tab, 1 = Without TDS tab

    if (selectedData?._id) {
      updateWalletById(selectedData._id, e)
        .then((res) => {
          // the service answers nothing when the server refused (it has already shown why): keep the dialog open
          if (!res) return;
          setOpenPaidTransaction(false);
          GetData();
          GetUserWalletDetails();
          ToastSuccess("Transaction updated successfully");
        })
        .catch((err) => {
          console.log(err);
          ToastError("Error updating transaction");
        });
    } else {
      PaidWallet(e)
        .then((res) => {
          if (!res) return;
          setOpenPaidTransaction(false);
          GetData();
          GetUserWalletDetails();
          ToastSuccess("Transaction created successfully");
        })
        .catch((err) => {
          console.log(err);
          ToastError("Error creating transaction");
        });
    }
  };

  const getRandomNumberUsingTimestamp = () => {
    return new Date().getTime();
  };

  const GetCompanyDetails = () => {
    GetMasterCompany().then((res) => {
      const modifiedCompanyDetails = res.data
        .map((e) => {
          if (e.isEnabled) {
            return {
              ...e,
              label: e.masterCompanyName,
              value: e._id,
            };
          } else {
            return null;
          }
        })
        .filter(Boolean);
      setCompanyDetails(modifiedCompanyDetails);
    });
  };

  const GetBankDetails = () => {
    GetBankDetailsByUserId(selectedData?.userId).then((res) => {
      const ModifiedData = res.data.bankDetails.map((e) => {
        return {
          ...e,
          label: e.bankAccountHolderName,
        };
      });
      setBankDetails(ModifiedData);
    });
  };

  const downloadFile = (key) => {
    if (!key) {
      ToastError("No records found!");
    } else {
      downloadFileByKey(key).then((res) => {
        const downloadURL = res?.data?.downloadURL || "";
        window.open(downloadURL);
      });
    }
  };

  const viewFile = (key) => {
    if (!key) {
      ToastError("No records found!");
    } else {
      downloadFileBase64ByKey(key).then((res) => {
        const downloadURL = res?.data?.downloadURL || "";
        const blobData = base64toBlob(downloadURL, "image/png");
        const blobUrl = URL.createObjectURL(blobData);
        const pdfWindow = window.open("", "_blank");
        pdfWindow?.document?.write(
          `<embed src="${blobUrl}" width="100%" height="100%" />`
        );
      });
    }
  };

  const AccountHandleFunction = (v, setFieldValue) => {
    setFieldValue("accountNumber", v?.accountNumber);
    setFieldValue("bankName", v?.bankName);
    setFieldValue("panNumber", v?.panNumber);
    setFieldValue("TDS", v?.tds);
    setTdsAmount(v?.tds);
    setLocalTDS(v?.tds || "");
    setSelectedBank(v);
    setSelectedAccHolderName(v?.bankAccountHolderName);

    if (amountVal && v?.tds) {
      TDSCalculateFunction(v?.tds, setFieldValue);
    }
  };

  const AmountCalculateFunction = (val, setFieldValue) => {
    const numericValue = parseFloat(val) || 0;
    setAmountVal(numericValue);

    if (value === 0) {
      // With TDS tab
      const TDSamount = Math.round((numericValue / 100) * tdsAmount);
      if (tdsAmount) {
        setFieldValue("TDSamount", TDSamount);
        setFieldValue("TransferAmount", Math.round(numericValue - TDSamount));
      } else {
        // no TDS % entered: nothing is deducted (the whole amount is not "TDS")
        setFieldValue("TDSamount", 0);
        setFieldValue("TransferAmount", numericValue);
      }
    } else {
      setFieldValue("TDSamount", 0);
      setFieldValue("TransferAmount", numericValue);
    }
  };

  const TDSCalculateFunction = (val, setFieldValue) => {
    const numericTds = parseFloat(val) || 0;
    setTdsAmount(numericTds);

    if (!amountVal) {
      setFieldValue("TDSamount", 0);
      setFieldValue("TransferAmount", 0);
      return;
    }

    if (value === 0) {
      // Only calculate for With TDS tab
      const TDSamount = Math.round((amountVal / 100) * numericTds);
      if (numericTds) {
        setFieldValue("TDSamount", TDSamount);
        setFieldValue("TransferAmount", Math.round(amountVal - TDSamount));
      } else {
        setFieldValue("TDSamount", 0);
        setFieldValue("TransferAmount", amountVal);
      }
    }
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    // Reset TDS-related fields when switching tabs
    if (formRef.current) {
      if (newValue === 1) {
        // Without TDS tab
        formRef.current.setFieldValue("TDS", "");
        formRef.current.setFieldValue("TDSamount", 0);
        formRef.current.setFieldValue("TransferAmount", amountVal || 0);
      }
    }
  };

  useEffect(() => {
    GetCompanyDetails();
    GetBankDetails();

    if (selectedData._id) {
      // Populate form with existing data for edit mode
      let selectedBankDetail = {};
      const accountNumber = selectedData?.accountNumber;

      if (accountNumber) {
        let bankDetails = selectedData.bankDetails;
        selectedBankDetail = bankDetails.find(
          (ele) => accountNumber == ele.accountNumber
        );
        setSelectedAccHolderName(selectedBankDetail?.bankAccountHolderName);
        setSelectedBank(selectedBankDetail);
      }

      // Set the active tab based on withTDS value
      setValue(selectedData.withTDS ? 0 : 1);

      setWalletCompany(selectedData?.walletCompany);
      setTdsAmount(selectedData?.TDS);
      setAmountVal(selectedData?.amount);

      setTimeout(() => {
        if (formRef.current) {
          formRef.current.setFieldValue(
            "transactionDate",
            dayjs(selectedData.transactionDate)
          );
          formRef.current.setFieldValue("TDS", selectedData?.TDS);
          formRef.current.setFieldValue("TDSamount", selectedData?.TDSamount);
          formRef.current.setFieldValue("amount", selectedData?.amount);
          formRef.current.setFieldValue("paymentType", selectedData?.paymentType);
          formRef.current.setFieldValue("remark", selectedData?.remark);
          formRef.current.setFieldValue(
            "walletCompanyId",
            selectedData?.walletCompanyId
          );
          formRef.current.setFieldValue("accountNumber", selectedData?.accountNumber);
          formRef.current.setFieldValue(
            "bankName",
            selectedData?.bankName || selectedBankDetail?.bankName
          );
          formRef.current.setFieldValue(
            "panNumber",
            selectedData?.panNumber || selectedBankDetail?.panNumber
          );
          formRef.current.setFieldValue(
            "TransferAmount",
            selectedData?.TransferAmount || selectedData?.amount
          );
          setLocalAmount(selectedData?.amount || "");
          setLocalTDS(selectedData?.TDS || "");
        }
      }, 0);
    }
  }, [selectedData]);

  // Set drawer width on mount and reset on unmount
  useEffect(() => {
    setDrawerWidth("50%");
    return () => {
      setDrawerWidth("25%");
    };
  }, []);

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#fff",

        }}
      >
        <Formik
          initialValues={initialValues}
          innerRef={formRef}
          validationSchema={
            functionType === "Received"
              ? receivedSchema
              : activeSchema
          }
          onSubmit={onSubmit}
        >
          {({ values, errors, touched, setFieldValue, setFieldTouched }) => (
            <Form style={{ display: "flex", flexDirection: "column" }}>
              <Grid container sx={{ padding: "12px", alignItems: "center" }}>
                <Grid item xs={10}>
                  <Typography variant="h6">
                    {functionType} to {PaidUserName}
                  </Typography>
                </Grid>
                <Grid item xs={2} sx={{ textAlign: "right" }}>
                  <CloseIcon
                    onClick={() => {
                      setDrawerWidth("25%");
                      setShowBankDetail(false);
                      setOpenPaidTransaction(false);
                    }}
                    sx={{ cursor: "pointer" }}
                  />
                </Grid>
              </Grid>

              <Box
                sx={{
                  px: 2,
                  maxHeight: "calc(100vh - 200px)", // strict scroll limit inside drawer
                  overflowY: "auto",
                  overflowX: "hidden",
                  pb: 2, scrollbarWidth: "none", // Firefox
                  msOverflowStyle: "none", // IE / Edge
                  "&::-webkit-scrollbar": {
                    display: "none", // Chrome, Safari
                  },

                }}
              >
                <Box sx={{ width: "100%" }}>

                  {/* ── RECEIVED: simple flat form, no tabs ── */}
                  {functionType === "Received" ? (
                    <Box sx={{ mt: 1 }}>
                      <Grid container spacing={1}>

                        {/* Transaction Date */}
                        <Grid item xs={12} sm={6}>
                          <label htmlFor="transactionDate" style={styles.label}>
                            Transaction Date <span style={{ color: "red" }}>*</span>
                          </label>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              name="transactionDate"
                              id="transactionDate"
                              className="Date_Picker w-100"
                              format="DD/MM/YYYY"
                              value={dayjs(values.transactionDate)}
                              onChange={(e) => {
                                setFieldValue("transactionDate", e?.$d || null);
                                setFieldTouched("transactionDate", true);
                              }}
                              sx={styles.input}
                            />
                          </LocalizationProvider>
                          {errors.transactionDate && touched.transactionDate && (
                            <span style={styles.errorText}>{errors.transactionDate}</span>
                          )}
                        </Grid>

                        {/* Bank */}
                        <Grid item xs={12} sm={6}>
                          <label htmlFor="bankName" style={styles.label}>
                            Bank <span style={{ color: "red" }}>*</span>
                          </label>
                          <Field
                            name="bankName"
                            id="bankName"
                            className="InputFiled"
                            style={{
                              ...styles.input,
                              borderColor:
                                errors.bankName && touched.bankName
                                  ? "#d32f2f"
                                  : undefined,
                            }}
                          />
                          {errors.bankName && touched.bankName && (
                            <span style={styles.errorText}>{errors.bankName}</span>
                          )}
                        </Grid>

                        {/* Remark */}
                        <Grid item xs={12} sm={6}>
                          <label htmlFor="remark" style={styles.label}>
                            Remark
                          </label>
                          <Field
                            name="remark"
                            id="remark"
                            className="InputFiled"
                            style={styles.input}
                          />
                        </Grid>

                        {/* Select Company */}
                        <Grid item xs={12} sm={6}>
                          <label htmlFor="walletCompanyId" style={styles.label}>
                            Select Company <span style={{ color: "red" }}>*</span>
                          </label>
                          <Autocomplete
                            className="AutoComplete_InputBox w-100"
                            name="walletCompanyId"
                            id="walletCompanyId"
                            onChange={(e, v) => {
                              setFieldValue("walletCompanyId", v?._id);
                              setWalletCompany(v?.masterCompanyName);
                              setFieldTouched("walletCompanyId", true);
                            }}
                            value={
                              companyDetails.find(
                                (cd) => cd.masterCompanyName === walletCompany
                              ) || null
                            }
                            options={companyDetails}
                            clearIcon={false}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                sx={{
                                  ...styles.input,
                                  "& .MuiOutlinedInput-root fieldset": {
                                    borderColor:
                                      errors.walletCompanyId && touched.walletCompanyId
                                        ? "#d32f2f"
                                        : undefined,
                                  },
                                }}
                              />
                            )}
                            isOptionEqualToValue={(option, value) =>
                              option._id === value?._id
                            }
                          />
                          {errors.walletCompanyId && touched.walletCompanyId && (
                            <span style={styles.errorText}>{errors.walletCompanyId}</span>
                          )}
                        </Grid>

                        {/* Amount */}
                        <Grid item xs={12} sm={6}>
                          <label htmlFor="amount" style={styles.label}>
                            Amount <span style={{ color: "red" }}>*</span>
                          </label>
                          <TextField
                            name="amount"
                            id="amount"
                            className="InputFiled"
                            sx={{
                              ...styles.input,
                              "& .MuiOutlinedInput-root fieldset": {
                                borderColor:
                                  errors.amount && touched.amount
                                    ? "#d32f2f"
                                    : undefined,
                              },
                            }}
                            value={values?.amount}
                            onChange={(e) => {
                              setFieldValue("amount", e.target.value);
                              setAmountVal(parseFloat(e.target.value) || 0);
                              setFieldTouched("amount", true);
                            }}
                          />
                          {errors.amount && touched.amount && (
                            <span style={styles.errorText}>{errors.amount}</span>
                          )}
                        </Grid>

                      </Grid>
                    </Box>
                  ) : (
                    /* ── PAID: tabs (With TDS / Without TDS) ── */
                    <>
                      <Tabs
                        value={value}
                        onChange={handleChange}
                        indicatorColor="primary"
                        textColor="primary"
                      >
                        <Tab
                          label="With TDS"
                          sx={{ textTransform: "none", fontSize: "14px" }}
                        />
                        <Tab
                          label="Without TDS"
                          sx={{ textTransform: "none", fontSize: "14px" }}
                        />
                      </Tabs>

                      <Box sx={{ mt: 2 }}>
                        {value === 0 && (
                          <TDSComponent
                            values={values}
                            errors={errors}
                            touched={touched}
                            setFieldValue={setFieldValue}
                            setFieldTouched={setFieldTouched}
                            bankDetails={bankDetails}
                            selectedAccHolderName={selectedAccHolderName}
                            AccountHandleFunction={AccountHandleFunction}
                            PaymentType={PaymentType}
                            companyDetails={companyDetails}
                            walletCompany={walletCompany}
                            setWalletCompany={setWalletCompany}
                            localAmount={localAmount}
                            setLocalAmount={setLocalAmount}
                            AmountCalculateFunction={AmountCalculateFunction}
                            localTDS={localTDS}
                            setLocalTDS={setLocalTDS}
                            TDSCalculateFunction={TDSCalculateFunction}
                            selectedBank={selectedBank}
                            showBankDetail={showBankDetail}
                            setShowBankDetail={setShowBankDetail}
                            setDrawerWidth={setDrawerWidth}
                          />
                        )}
                        {value === 1 && (
                          <WithoutTDSComponent
                            values={values}
                            errors={errors}
                            touched={touched}
                            setFieldValue={setFieldValue}
                            setFieldTouched={setFieldTouched}
                            companyDetails={companyDetails}
                            walletCompany={walletCompany}
                            setWalletCompany={setWalletCompany}
                            setAmountVal={setAmountVal}
                          />
                        )}

                        <BankDetailsPanel
                          selectedBank={selectedBank}
                          showBankDetail={showBankDetail}
                          downloadFile={downloadFile}
                          viewFile={viewFile}
                        />
                      </Box>
                    </>
                  )}
                </Box>
              </Box>

              {/* ── Action Buttons (outside scroll area, always visible) ── */}
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderTop: "1px solid #e0e0e0",
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                  backgroundColor: "#fff",
                  flexShrink: 0,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => {
                    setDrawerWidth("25%");
                    setShowBankDetail(false);
                    setOpenPaidTransaction(false);
                  }}
                  sx={{ textTransform: "none" }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ textTransform: "none" }}
                >
                  {selectedData?._id ? "Update" : "Confirm"}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default PaidTransaction;
